/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure, Firebase/Sentry-decoupled instrumentation for the OTP (Firebase Phone
 * Auth) send + verify flow's previously-SILENT failure points (Phase-0 audit).
 *
 * The headline case is `auth/too-many-requests` — the iOS "white page" that is
 * actually anti-abuse rate-limiting, today mapped to a generic string with NO
 * console and NO Sentry. It arrives two ways: the web path rejects with an
 * `auth/*` code; the native `phoneVerificationFailed` event carries only a raw
 * `message` (no code). `isRateLimitError` classifies BOTH.
 *
 * Each recorder takes an `OtpInstrumentationSink` (real Sentry adapter in the
 * sheet/AuthContext; a mock in tests — CLAUDE.md DI pattern, mirrors
 * sosInstrumentation.ts / wmhInstrumentation.ts).
 *
 * PRIVACY: message strings are static step identifiers. The phone number
 * (E.164) is NEVER passed to the sink — only the Firebase error code, the
 * transport path ("native" | "web"), and booleans go into structured
 * tags/extra. Error codes are Firebase enum strings, not user data.
 */

import { describeError, type SosBreadcrumb } from "./sosEventWrite";

type CaptureContext = {
  level?: "info" | "warning" | "error";
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

export interface OtpInstrumentationSink {
  addBreadcrumb: (b: SosBreadcrumb) => void;
  captureMessage: (message: string, ctx: CaptureContext) => void;
  captureException: (err: unknown, ctx: CaptureContext) => void;
}

const AREA = "auth";

/**
 * True when a phone-auth send failure is Firebase's anti-abuse rate limit —
 * from either an `auth/too-many-requests` code (web) or the raw native message
 * ("We have blocked all requests from this device due to unusual activity…" /
 * "too many …"). This is the "white page" root cause.
 */
export function isRateLimitError(input: {
  code?: string;
  message?: string;
}): boolean {
  const code = input.code ?? "";
  if (code === "auth/too-many-requests") return true;
  const msg = (input.message ?? "").toLowerCase();
  return (
    msg.includes("too many") ||
    msg.includes("unusual activity") ||
    msg.includes("blocked all requests")
  );
}

/** Transport path a send attempt used. */
export type OtpSendPath = "native" | "web";

/**
 * A send-code attempt failed. The rate-limit ("white page") case is captured
 * distinctly at warning level; every other send failure is captured too
 * (previously the send path had NO Sentry at all). UI copy is unchanged — this
 * is logging only. Never receives the phone number.
 */
export function recordSendFailed(
  sink: OtpInstrumentationSink,
  input: { code?: string; message?: string; path: OtpSendPath },
): void {
  const rateLimited = isRateLimitError(input);
  sink.captureMessage(
    rateLimited ? "otp.send_rate_limited" : "otp.send_failed",
    {
      level: "warning",
      tags: {
        area: AREA,
        phase: "send_code",
        path: input.path,
        code: input.code || "none",
        rateLimited: String(rateLimited),
      },
      extra: {
        // message can carry a Firebase anti-abuse string — a server message,
        // never the user's phone number.
        message: input.message,
      },
    },
  );
}

/**
 * APNs silent-verification wasn't available, so the flow silently degraded from
 * the native silent-push path to the web reCAPTCHA path. Not a hard failure —
 * breadcrumb, so the trail explains why a reCAPTCHA appeared on iOS.
 */
export function recordApnsFallbackToWeb(
  sink: OtpInstrumentationSink,
  apnsStatus: string,
): void {
  sink.addBreadcrumb({
    category: AREA,
    level: "warning",
    message: "otp.apns_unavailable_fallback_web",
    data: { apnsStatus },
  });
}

/**
 * verifyPhone was re-entered while a sign-in was already IN_FLIGHT, so it
 * returned early and resolved as if it succeeded while doing nothing. Captured
 * (warning) so this silent no-op is visible.
 */
export function recordVerifyReentrancySkipped(
  sink: OtpInstrumentationSink,
  state: string,
): void {
  sink.captureMessage("otp.verify_reentrancy_skipped", {
    level: "warning",
    tags: { area: AREA, phase: "verify" },
    extra: { state },
  });
}

/**
 * The user submitted a code but there was no verificationId to build the
 * credential — the native `phoneCodeSent` event never fired (or the ref was
 * cleared). Console-only today. Captured (warning) so this dead-end is visible.
 */
export function recordMissingVerificationId(
  sink: OtpInstrumentationSink,
): void {
  sink.captureMessage("otp.missing_verification_id", {
    level: "warning",
    tags: { area: AREA, phase: "verify" },
  });
}

/**
 * The post-credential profile write (`ensureUserProfile`) threw and the flow
 * fail-opened (setNeedsUsername(false)) — console-only today, so a profile
 * write failure is invisible. Captured as an exception with the extracted code.
 */
export function recordProfileEnsureFailed(
  sink: OtpInstrumentationSink,
  err: unknown,
  ctx: { uid: string },
): void {
  const info = describeError(err);
  sink.captureException(err, {
    tags: { area: AREA, phase: "post_credential_profile" },
    extra: { uid: ctx.uid, code: info.code, message: info.message },
  });
}
